import { useMemo, useState } from 'react'
import { Check, ChevronDown, Search } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Input } from '@/shared/components/ui/input'
import { cn } from '@/shared/utils/cn'

export interface SearchableSelectOption {
  value: number
  label: string
  /** Texto extra para la búsqueda (ej. código de provincia). */
  keywords?: string
}

interface SearchableSelectProps {
  value: number | null | undefined
  onChange: (value: number) => void
  options: SearchableSelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  /** Mensaje cuando no hay opciones (ej. "Selecciona primero un país"). */
  emptyMessage?: string
  disabled?: boolean
  loading?: boolean
  /** Marca el control como inválido (aria-invalid). */
  invalid?: boolean
  className?: string
}

/**
 * Select único con búsqueda. Trigger estilo Select + Popover con input de
 * búsqueda y lista filtrable; al elegir una opción se cierra el popover. Mismo
 * lenguaje visual que `CategoriesSelect` pero de selección simple. Pensado para
 * catálogos largos (provincias, municipios) donde el Select nativo no escala.
 * Reutilizable en cualquier formulario.
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Selecciona una opción',
  searchPlaceholder = 'Buscar...',
  emptyMessage = 'No hay opciones disponibles',
  disabled,
  loading,
  invalid,
  className,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return options
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.keywords?.toLowerCase().includes(q) ?? false),
    )
  }, [options, search])

  const selected = useMemo(
    () => (value != null && value > 0 ? options.find((o) => o.value === value) : undefined),
    [options, value],
  )

  const handleSelect = (next: number) => {
    onChange(next)
    setOpen(false)
    setSearch('')
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setSearch('')
      }}
    >
      <PopoverTrigger
        type="button"
        disabled={disabled || loading}
        aria-invalid={invalid}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-line bg-panel px-3 text-sm text-ink',
          'transition-colors focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'aria-[invalid=true]:border-danger aria-[invalid=true]:focus:ring-danger/40',
          className,
        )}
      >
        <span className={cn('truncate', !selected && 'text-ink-subtle')}>
          {loading ? 'Cargando…' : (selected?.label ?? placeholder)}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted" />
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
        <div className="relative border-b border-line p-2">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-subtle" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-8 pl-8 text-xs"
            autoFocus
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-ink-muted">
              {options.length === 0 ? emptyMessage : 'Sin resultados'}
            </p>
          ) : (
            filtered.map((o) => {
              const isSelected = o.value === value
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => handleSelect(o.value)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                    'hover:bg-surface focus-visible:bg-surface focus-visible:outline-none',
                  )}
                  aria-pressed={isSelected}
                >
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                    {isSelected && <Check className="h-4 w-4 text-brand" strokeWidth={3} />}
                  </span>
                  <span className="flex-1 truncate text-ink">{o.label}</span>
                </button>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
